import React, { useState } from 'react';
import { accounting0, EM_DASH } from '../../utils/money';
import './Scorecard.css';

// The weekly scorecard, prefilled.
//
// Two states and no others, exactly as the approved mockup has it: a BOX means a
// person typed it, plain text means the system already knew it. Only Site Visits and
// the four Targets are boxed; everything else is counted from the lead records, the
// booking tracker or the deal table below.
//
// Every figure comes off `sec.scorecardFigures`, built server-side by
// backend/services/scorecardFigures.js. Nothing is recomputed here -- a second
// implementation in the browser is a second set of numbers that can disagree with
// the frozen ones. When `scorecardFigures` is absent the component renders NOTHING:
// that is how a meeting frozen before this feature existed reads, and a table of
// zeros would be a claim the data cannot support.
//
// It also renders nothing when the server says `hidden` -- somebody whose whole month
// was catering has no event-space leads, deals or bookings, and a column of zeros
// would read as poor performance rather than as a different job. The judgement is the
// server's, not this component's, so a frozen meeting hides exactly what it hid on the
// day. Their revenue tiles, insights and deal table are untouched either way.
//
// ENQUIRY, TWICE, ON PURPOSE. The Enquiries tile above counts EVERY project so the
// meeting can never disagree with the Lead Report. This table counts event-space
// projects only, because it measures venue deals -- hence the row label. The two are
// different questions, not a contradiction, and the label is what says so.

const num = (v) => (v == null || v === '' || isNaN(Number(v)))
  ? EM_DASH : Number(v).toLocaleString('en-MY', { maximumFractionDigits: 0 });
const pct = (v) => (v == null || isNaN(Number(v))) ? EM_DASH : `${(Number(v) * 100).toFixed(1)}%`;
const rm = (v) => accounting0(v);

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

// '2026-08' or '2026-08-21' -> 'August 2026'. Parsed by hand rather than through
// Date, which would shift the month across a timezone at the start of the month.
export function monthLabel(value) {
  const m = /^(\d{4})-(\d{2})/.exec(String(value ?? ''));
  if (!m) return '';
  const i = Number(m[2]) - 1;
  return MONTHS[i] ? `${MONTHS[i]} ${m[1]}` : '';
}

// A typed figure. Boxed whether or not this reader can edit it -- the box says
// "somebody typed this", which stays true when the box is not yours to fill.
function Typed({ label, value, editable, money, onCommit }) {
  const [draft, setDraft] = useState(null);   // null = showing the stored value
  const empty = value == null || value === '';
  const shown = money ? rm(value) : num(value);

  if (!editable) {
    return <span className={`ml-sc-typed${empty ? ' is-empty' : ''}`}>{shown}</span>;
  }
  const commit = () => {
    if (draft == null) return;
    const next = draft.trim() === '' ? null : Number(draft);
    setDraft(null);
    if (Number.isNaN(next)) return;
    if (String(next ?? '') !== String(value ?? '')) onCommit(next);
  };
  return (
    <input className={`ml-sc-typed ml-sc-input${empty && draft == null ? ' is-empty' : ''}`}
           aria-label={label} inputMode="decimal"
           value={draft != null ? draft : (empty ? '' : String(value))}
           placeholder={EM_DASH}
           onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, ''))}
           onBlur={commit}
           onKeyDown={(e) => {
             if (e.key === 'Enter') e.currentTarget.blur();
             else if (e.key === 'Escape') setDraft(null);
           }} />
  );
}

export default function Scorecard({ sec, month, editable = false, onSave }) {
  const figures = sec && sec.scorecardFigures;
  if (!figures || figures.hidden) return null;

  const t = figures.targets || {};
  const venues = figures.venues || [];
  // The typed store, never the read-time copy: `sec.scorecard` has enqWeek replaced
  // by the lead count, and saving that back would persist a synced number as a typed one.
  const stored = (sec.checkins && sec.checkins.scorecard) || {};
  const svWeek = stored.svWeek ?? null;
  const canEdit = editable && typeof onSave === 'function';

  const save = (path, value) => {
    const next = JSON.parse(JSON.stringify(stored));
    if (path.startsWith('t.')) { next.t = { ...(next.t || {}) }; next.t[path.slice(2)] = value; }
    else next[path] = value;
    onSave(next);
  };
  const typed = (path, label, value, money) => (
    <Typed label={label} value={value} money={money} editable={canEdit}
           onCommit={(v) => save(path, v)} />
  );

  // The box types THIS week's visits; the row's ratio is measured over the month, so
  // the month total is stated beside it whenever the two differ.
  const monthSv = figures.siteVisits;
  const svNote = (monthSv != null && Number(monthSv) !== Number(svWeek ?? NaN))
    ? <span className="ml-sc-mtd">month {num(monthSv)}</span> : null;

  // Each venue line states the enquiries it converted from, so the rate beside it can
  // be checked. `enquiries: null` is a meeting frozen before the split existed -- it
  // says nothing rather than 0. W Nexus shares one pool across up to three codes
  // (leads never say which), so a shared line names the pool and shows no rate.
  const venueEnq = (v) => {
    if (v.enquiries == null) return null;
    return (
      <span className="ml-sc-note" data-testid={`sc-venue-enq-${v.code}`}>
        {v.enquiriesShared
          ? `${num(v.enquiries)} enquiries across W Nexus`
          : `${num(v.enquiries)} enquiries`}
      </span>
    );
  };

  const label = monthLabel(month);
  const sourceNote = figures.source === 'tracker'
    ? 'from the booking tracker' : 'from the deal list';

  return (
    <div className="ml-sc" data-testid="scorecard">
      <div className="ml-sc-head">
        <span className="ml-eyebrow">Scorecard{label ? ` — ${label}` : ''}</span>
        <span className="ml-sc-source" data-testid="scorecard-source">{sourceNote}</span>
      </div>

      <table className="ml-sc-table">
        <thead>
          <tr><th className="k" scope="col"><span className="ml-sc-sr">Measure</span></th>
              <th scope="col">Target</th><th scope="col">Actual</th></tr>
        </thead>
        <tbody>
          <tr>
            <td className="k">Enquiry — event space <span className="ml-sc-note">excludes catering</span></td>
            <td>{typed('t.enq', 'Enquiry target', t.enq)}</td>
            <td className="auto" data-testid="sc-enquiries">{num(figures.eventSpaceEnquiries)}</td>
          </tr>
          <tr>
            <td className="k">Site visits</td>
            <td>{typed('t.sv', 'Site visits target', t.sv)}</td>
            <td data-testid="sc-site-visits">{typed('svWeek', 'Site visits this week', svWeek)}{svNote}</td>
          </tr>
          <tr>
            <td className="k">Site visit %</td>
            <td className="derived">{pct(t.siteVisitPct)}</td>
            <td className="derived" data-testid="sc-site-visit-pct">{pct(figures.siteVisitPct)}</td>
          </tr>

          <tr className="tot">
            <td className="k">Done deals — event space</td>
            <td>{typed('t.dd', 'Done deals target', t.dd)}</td>
            <td className="auto" data-testid="sc-done-deals">{num(figures.doneDeals)}</td>
          </tr>
          {/* A deal spanning two venues shows in FULL on both lines while counting
              once in the total above, so these can legitimately sum to more. */}
          {venues.map((v, i) => (
            <tr className={'sub' + (i === venues.length - 1 ? ' last' : '')} key={`dd-${v.code}`}>
              <td className="k">{v.label}{venueEnq(v)}</td>
              <td className="derived">{EM_DASH}</td>
              <td className="auto" data-testid={`sc-done-deals-${v.code}`}>{num(v.doneDeals)}</td>
            </tr>
          ))}
          <tr>
            <td className="k">Done deal rate <span className="ml-sc-note">excl. W Nexus</span></td>
            <td className="derived">{pct(t.doneDealRate)}</td>
            <td className="derived" data-testid="sc-done-deal-rate">{pct(figures.doneDealRate)}</td>
          </tr>

          <tr className="tot">
            <td className="k">Captured revenue <span className="ml-sc-note">RM</span></td>
            <td>{typed('t.rev', 'Captured revenue target', t.rev, true)}</td>
            <td className="auto" data-testid="sc-captured">{rm(figures.captured)}</td>
          </tr>
          {venues.map((v, i) => (
            <tr className={'sub' + (i === venues.length - 1 ? ' last' : '')} key={`cap-${v.code}`}>
              <td className="k">{v.label}</td>
              <td className="derived">{EM_DASH}</td>
              <td className="auto" data-testid={`sc-captured-${v.code}`}>{rm(v.captured)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="ml-sc-legend">
        <span><i className="ml-sc-chip is-auto" /> filled by the system</span>
        <span><i className="ml-sc-chip is-typed" /> typed</span>
        <span><i className="ml-sc-chip is-derived" /> calculated</span>
      </div>
    </div>
  );
}
